import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { trackUserAttribute } from '@forwardfinancing/analytics-frontend-library';
import { Form as ReduxForm } from 'react-redux-form';
import ProgressIndicator from './ProgressIndicator';
import FormFields from './FormFields';
import { stipUploadTags, signedApplicationTag, bankStatementsTag } from '../../../constants/stipUploadTags';
import {
  isFirstSection,
  isLastSection,
  isSubmissionSection,
  nextSection,
  previousSection
} from '../../../lib/sectionUtils';
import ModalPortal from '../../portals/ModalPortal';
import {
  HOMEPAGE_LINK,
  LOADING_MESSAGE,
  SUBMISSION_COMPLETE_MESSAGE,
  SUBMIT_ERROR_MESSAGE
} from '../../../constants/formModalMessages';
import linkUtils from '../../../lib/linkUtils';
import { uploadErrorInformation } from '../../../lib/emailUtils';
import { SOURCES } from '../../../constants/global.js';
import FormNavigation from './FormNavigation';
import sharedFormProps from './SharedFormProps';

export class Form extends Component {
  componentDidMount() {
    if (this.shouldReloadForm()) {
      window.location.reload();
    }
  }

  shouldReloadForm() {
    const {submissionInfo: {form: {files}}} = this.props;

    return files.length !== 0;
  }

  trackBasedOnSource() {
    const { label, step, dba, shortenedForm } = this.props;

    linkUtils.trackStep({
      dba,
      label,
      step,
      source: window,
      ...shortenedForm && {[SOURCES.SHORT]: true}
    });
  }

  goToNextStep() {
    const newSection = nextSection(this.props);
    // if you are on step 0, 1, 2, or 3 - just go up one step
    const {submissionInfoActions: { setSubmissionFormInfo }} = this.props;
    setSubmissionFormInfo(newSection, this.props.dba);
  }

  async submitInformation() {
    if (isSubmissionSection(this.props)) {
      try {
        const { postSubmission, uuid } = this.props;

        await postSubmission({...this.props.submission, ...uuid && { uuid }});
        trackUserAttribute({attribute: 'submission', count: false});
      } catch (err) {
        this.props.showModal({
          message: SUBMIT_ERROR_MESSAGE,
          className: "form-message",
          error: true,
          link: HOMEPAGE_LINK
        });
      }
    }
  }

  /**
   * This handler is called every time Next is clicked on every page
   */
  async handleSubmitOfForm() {
    this.goToNextStep();

    await this.submitInformation();
  }

  async handleFinalSubmit() {
    const { id } = this.props.submissionInfo.submission.info;

    if (this.hasSubmittedLeadOrRenewal()) {
      const {files} = this.props.submissionInfo.form;
      this.props.submissionInfoActions.setSubmissionInfoLoading();
      this.props.showModal({
        message: LOADING_MESSAGE,
        className: "form-message"
      });

      await Promise.all(files.map((file) => this.tryToAttach(file, id)));

      this.displayFinalMessage();
    }
  }

  onCloseModal() {
    const {
      failedUploads,
      hideModal,
      clearSubmissionAttachments,
      submissionInfoActions: { setSubmissionFormInfo }
    } = this.props;

    hideModal();

    if (failedUploads.length > 0) {
      this.resetAllFormFields();
      setSubmissionFormInfo(1);
    }

    clearSubmissionAttachments();
  }

  resetAllFormFields() {
    Object.keys(this.props.submission).forEach(field => {
      this.props.resetForm(`submission.${field}`);
    });
  }

  async tryToAttach(file, id) {
    try {
      const { postSubmissionAttachments } = this.props;
      await postSubmissionAttachments(file, id);
    } catch (err) {
      const { failedUploads, leadId, accountManagerEmail } = this.props;

      this.props.showModal({
        message: uploadErrorInformation({
          source: linkUtils.getLocationName(window),
          failedUploads,
          leadId,
          accountManagerEmail
        }),
        link: HOMEPAGE_LINK,
        error: true,
        className: "form-message-upload-error",
        onClose: this.onCloseModal.bind(this)
      });
    }
  }

  displayFinalMessage() {
    if (this.props.failedUploads.length === 0) {
      this.props.submissionInfoActions.clearSubmissionInfoLoading();
      this.props.showModal({
        className: 'form-message',
        message: SUBMISSION_COMPLETE_MESSAGE,
        link: HOMEPAGE_LINK
      });
    }
  }

  hasOneStep() {
    return isFirstSection(this.props) && isLastSection(this.props);
  }

  hasSubmittedLeadOrRenewal() {
    return !!this.props.submissionInfo.submission.info.id;
  }

  hasOneStepAndNoSubmission() {
    return this.hasOneStep() && !this.hasSubmittedLeadOrRenewal();
  }

  async handleSubmitBasedOnStep() {
    this.trackBasedOnSource();

    if (this.hasOneStepAndNoSubmission()) {
      await this.handleSubmitOfForm();
    }

    if (!isLastSection(this.props)) {
      return this.handleSubmitOfForm();
    }

    this.handleFinalSubmit();
  }

  handleBack() {
    const prevSection = previousSection(this.props);

    this.props.submissionInfoActions.setSubmissionFormInfo(prevSection);
  }

  getSectionTitle(section) {
    const headers = {'1': 'Business Details', '2': 'Application Info', '3': 'Financing Info', '4': 'Upload Documents'}
    return headers[`${section}`]
  }

  render() {
    const {
      progressText,
      submissionInfo: {
        form: { files, section }
      },
      totalSections,
    } = this.props;

    return (
      <div className="submission-form column page">
        <div>
          <div className="submission-form__header title is-4">
            {this.props.title}
          </div>
          {
            progressText &&
            <ProgressIndicator
              sectionActive={section}
              totalSections={totalSections}
              progressText={progressText}
            />
          }
        </div>
        <div className="submission-form__form-wrap">

          <ReduxForm
            className="submission-form__content column is-6-desktop"
            model="submission"
            onSubmit={this.handleSubmitBasedOnStep.bind(this)}
          >
            <div className="submission-form__title">
              <h2>{this.getSectionTitle(section)}</h2>
              <hr/>
            </div>
            <FormFields
              {...this.props}
              className={`submission-form__section-${section}-wrap`}
              sectionKey={`section${section}`}
              fileTaggerDropdownOptions={[signedApplicationTag, bankStatementsTag,...stipUploadTags]}
            />

            <FormNavigation
              isDisabledSubmitButton={files.length === 0}
              isLoading={this.props.submissionInfo.submission.isLoading}
              handleBack={this.handleBack.bind(this)}
              section={section}
              showBackButton={
                !isLastSection(this.props) && !isFirstSection(this.props)
              }                                                                              x
              showNextButton={!isLastSection(this.props)}
              showFinalSubmitButton={isLastSection(this.props)}
            />
          </ReduxForm>
        </div>

        <ModalPortal />
      </div>
    );
  }
}

Form.propTypes = {
  ...sharedFormProps,
  accountDetailsActions: PropTypes.object,
  addressActions: PropTypes.object,
  fields: PropTypes.object.isRequired,
  postSubmission: PropTypes.func.isRequired,
  progressText: PropTypes.object,
  title: PropTypes.string.isRequired,
  totalSections: PropTypes.number.isRequired,
  uploadInstructions: PropTypes.object
};

export default Form;
